import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Search, ChevronUp, ChevronDown, ChevronsUpDown, Inbox } from 'lucide-react';

export interface TableColumn<T = unknown> {
  key: string;
  label: string;
  render?: (value: unknown, row: T) => ReactNode;
  className?: string;
  sortable?: boolean;
}

export interface DataTableProps<T extends Record<string, unknown>> {
  columns: TableColumn<T>[];
  data: T[];
  keyField?: keyof T | string;
  emptyMessage?: string;
  actions?: (row: T) => ReactNode;
  sortable?: boolean;
  isFilter?: boolean;
}

function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  if (!path) return obj;
  return path.split('.').reduce((acc: unknown, key) => (acc != null && typeof acc === 'object' ? (acc as Record<string, unknown>)[key] : undefined), obj);
}

function compareValues(a: unknown, b: unknown): number {
  if (a == null) return 1;
  if (b == null) return -1;
  const numA = Number(a);
  const numB = Number(b);
  if (!Number.isNaN(numA) && !Number.isNaN(numB)) return numA - numB;
  const dateA = new Date(a as string | number).getTime();
  const dateB = new Date(b as string | number).getTime();
  if (!Number.isNaN(dateA) && !Number.isNaN(dateB)) return dateA - dateB;
  return String(a).localeCompare(String(b));
}

export default function DataTable<T extends Record<string, unknown>>({
  columns,
  data,
  keyField = 'id',
  emptyMessage = 'Aucune donnée',
  actions,
  sortable = false,
  isFilter = false,
}: DataTableProps<T>) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [filterText, setFilterText] = useState('');

  const getKey = (row: T) => {
    const k = typeof keyField === 'string' ? keyField : (keyField as keyof T);
    return String(row[k] ?? Math.random());
  };

  const filteredData = useMemo(() => {
    if (!filterText.trim()) return data;
    const q = filterText.toLowerCase();
    return data.filter((row) =>
      columns.some((col) =>
        String(getNestedValue(row as Record<string, unknown>, col.key)).toLowerCase().includes(q)
      )
    );
  }, [data, filterText, columns]);

  const sortedData = useMemo(() => {
    if (!sortKey) return filteredData;
    return [...filteredData].sort((a, b) => {
      const aVal = getNestedValue(a as Record<string, unknown>, sortKey);
      const bVal = getNestedValue(b as Record<string, unknown>, sortKey);
      const cmp = compareValues(aVal, bVal);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filteredData, sortKey, sortDir]);

  const handleSort = (key: string) => {
    if (!sortable) return;
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
      {isFilter && (
        <div className="border-b border-slate-200 bg-slate-50/80 px-4 py-3">
          <div className="relative max-w-xs">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="pro-input w-full pl-9"
            />
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50/80">
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 ${col.className ?? ''} ${sortable && col.sortable !== false ? 'cursor-pointer select-none hover:text-slate-700' : ''}`}
                  onClick={() => sortable && col.sortable !== false && handleSort(col.key)}
                >
                  <div className="flex items-center gap-1.5">
                    {col.label}
                    {sortable && col.sortable !== false && (
                      <span className="inline-flex text-slate-400">
                        {sortKey === col.key ? (
                          sortDir === 'asc' ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )
                        ) : (
                          <ChevronsUpDown className="h-4 w-4" />
                        )}
                      </span>
                    )}
                  </div>
                </th>
              ))}
              {actions && (
                <th className="px-5 py-3.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {sortedData.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (actions ? 1 : 0)}
                  className="px-5 py-12 text-center text-sm text-slate-500"
                >
                  <div className="flex flex-col items-center gap-2">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                      <Inbox className="h-6 w-6 text-slate-400" />
                    </div>
                    <p className="font-medium text-slate-700">{emptyMessage}</p>
                  </div>
                </td>
              </tr>
            ) : (
              sortedData.map((row) => (
                <tr
                  key={getKey(row)}
                  className="bg-white transition-colors hover:bg-slate-50/70"
                >
                  {columns.map((col) => {
                    const value = col.key.includes('.')
                      ? getNestedValue(row as Record<string, unknown>, col.key)
                      : row[col.key];
                    return (
                      <td
                        key={col.key}
                        className={`whitespace-nowrap px-5 py-3.5 text-sm text-slate-800 ${col.className ?? ''}`}
                      >
                        {col.render ? col.render(value, row) : value != null ? String(value) : '—'}
                      </td>
                    );
                  })}
                  {actions && (
                    <td className="whitespace-nowrap px-5 py-3.5 text-right text-sm">
                      {actions(row)}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
